import React, { useCallback, useEffect, useState } from 'react';
import {
  atualizarPreset,
  fetchFornecedores,
  fetchGestores,
  fetchPainelAlertas,
  fetchPresets,
  importarFornecedores,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { Fornecedor, GestorCompras, PainelAlertas, Preset } from '../../../interfaces/PortalCotacoes';
import ConfirmacaoModal from '../components/ConfirmacaoModal';
import FornecedorDetalhesModal from '../components/FornecedorDetalhesModal';
import GerenciarGestoresModal from '../components/GerenciarGestoresModal';
import EntregasSection from '../components/EntregasSection';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import Paginacao from '../../../components/paginacao/Paginacao';
import useIsAdmin from '../../../hooks/useIsAdmin';
import { useIsTi } from '../../../hooks/useUserType';
import FormasPagamentoSection from '../components/FormasPagamentoSection';
import RespostaFornecedorSection from '../components/RespostaFornecedorSection';
import CobrancaNoErpSection from '../components/CobrancaNoErpSection';
import AlertasEtapaSection from '../components/AlertasEtapaSection';
import EmailsFornecedorSection from '../components/EmailsFornecedorSection';
import { notify } from '../../../shared/feedback/notify';
import { formatPhoneBr } from '../../../utils/phoneMask';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../admin-sistema/pages/css/adminSistema.css';
// Tabela e paginação do padrão do sistema (mesmo CSS da Recepção e da lista de cotações) e,
// desde a padronização do esqueleto, também o corpo e o cabeçalho da página
// (`recepcao-conte-do`, `recepcao-header-row`, `recepcao-title`) — os mesmos das telas de
// Administração do Sistema. O import é explícito: não se depende de CSS de outra tela.
import '../../recepcao/pages/Recepcao.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Configuração do módulo COTAÇÕES (rota `/admin/sistema/cotacoes`).
 *
 * Mudou de lugar e de aparência na at-73:
 *
 * - **Lugar**: era `/portal-cotacoes/config`, alcançável só por URL digitada (nenhuma
 *   tela linkava para ela). Agora é um módulo da Administração do Sistema.
 * - **Aparência**: usava um design paralelo próprio (classes `pc-*`, 483 linhas de CSS
 *   local). Passou a usar as classes da Administração do Sistema — as telas de
 *   configuração do sistema têm de parecer a mesma tela.
 *
 * A carga também foi corrigida (regra da at-60): `fetchGestores` e `fetchPainelAlertas`
 * tinham `.catch(() => [])` e `.catch(() => null)`, então um 403 virava "nenhum gestor
 * cadastrado" — uma AFIRMAÇÃO DE NEGÓCIO derivada de erro de API. E o 403 aqui é
 * esperado: a lista de gestores é restrita a ADMIN, e esta tela também abre para
 * COMPRA. Hoje cada bloco sabe se a própria carga falhou e diz isso.
 */

type Aba = 'presets' | 'fornecedores' | 'gestores' | 'resposta' | 'pagamento' | 'alertas' | 'emails';

/** 20 por página é o padrão de listagem do sistema. */
const FORNECEDORES_POR_PAGINA = 20;

/** Gestores são poucos (um punhado de pessoas): 10 por página nunca deve paginar de fato. */
const GESTORES_POR_PAGINA = 10;

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Por que os contatos ficam aqui',
    conteudo: (
      <p>
        O ERP guarda a <strong>identidade</strong> do fornecedor (CNPJ, razão social), mas não os contatos — o
        cadastro de telefone e e-mail lá está praticamente vazio. Por isso o portal mantém a própria base de
        contatos: é dela que saem os convites. Fornecedor sem contato não recebe cotação.
      </p>
    ),
  },
  {
    titulo: 'O que os presets fazem',
    conteudo: (
      <p>
        Os presets definem o peso de <strong>preço e prazo</strong> no score que apoia a comparação. Ao criar uma
        cotação você escolhe um preset, e os pesos ficam <strong>congelados</strong> nela: editar o preset depois
        não altera cotações já enviadas nem o histórico.
      </p>
    ),
  },
  {
    titulo: 'A nota de qualidade ainda não pesa',
    conteudo: (
      <p>
        O peso de qualidade nasce em <strong>zero</strong> porque ainda não existe fonte de dado confiável. A nota
        pode ser cadastrada desde já; ela só passa a influenciar o score quando um administrador subir o peso de
        qualidade em algum preset.
      </p>
    ),
  },
  {
    titulo: 'Gestores de compras',
    conteudo: (
      <div>
        <p>
          Só quem está nesta lista aprova itens escolhidos acima do menor preço — inclusive administradores,
          que <strong>não têm passe livre</strong> aqui.
        </p>
        <p>
          A lista é mantida por um <strong>administrador</strong>, no botão{' '}
          <strong>Gerenciar gestores</strong> da aba: ele escolhe entre os usuários ativos de{' '}
          <strong>Compras</strong> e <strong>Administrador</strong>, e o nome exibido vem do cadastro de
          usuários do sistema. Outros perfis veem a lista, mas não a alteram.
        </p>
        <p>
          Só esses dois papéis aparecem porque <strong>são os que abrem o Portal</strong>. Alguém de outro
          papel até poderia ser gravado como gestor, mas não veria o aviso nem conseguiria abrir a fila de
          aprovações — e a cotação ficaria parada esperando uma decisão que ninguém poderia tomar.
        </p>
        <p>
          A mudança vale para as aprovações <strong>daqui em diante</strong>. Cotação que já está aguardando
          aprovação continua aguardando e passa a depender de quem ficou na lista; decisão já registrada não
          é revista.
        </p>
        <p>
          <strong>Lista vazia trava</strong> toda cotação com item acima do menor preço — por isso o sistema{' '}
          <strong>recusa esvaziá-la enquanto houver item aguardando aprovação</strong>, e recusa também o
          fechamento de cesta com item acima do menor preço quando não há nenhum gestor cadastrado. Nos dois
          casos a tela explica o motivo. Trocar todos os gestores de uma vez continua liberado: a aprovação não
          pertence a um gestor específico, qualquer um da lista decide.
        </p>
      </div>
    ),
  },
];

const PortalCotacoesConfig: React.FC = () => {
  const [aba, setAba] = useState<Aba>('presets');
  const [ajudaAberta, setAjudaAberta] = useState(false);
  /*
   * `PUT /gestores` é ADMIN PURO no Portal (nem TI). Oferecer o botão a quem não pode
   * gravar daria um caminho que só devolve 403 — o mesmo erro de UX que o gating de TI
   * existe para evitar (ADR 0012). A imposição real é do backend; isto é a UI honesta.
   */
  const ehAdmin = useIsAdmin();
  // `PUT /settings/:key` é ADMIN ou TI (o Portal segue o ADR 0012: TI configura, não opera).
  const ehTi = useIsTi();

  const [presets, setPresets] = useState<Preset[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  // Paginação da aba Fornecedores: a base real tem ~1000 registros (importados do ERP),
  // então a lista inteira numa página só era rolagem infinita de cards.
  const [totalFornecedores, setTotalFornecedores] = useState(0);
  const [paginaFornecedores, setPaginaFornecedores] = useState(1);
  const [paginaGestores, setPaginaGestores] = useState(1);
  const [gestores, setGestores] = useState<GestorCompras[]>([]);
  const [alertas, setAlertas] = useState<PainelAlertas | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);

  // Erro POR BLOCO: cada aba precisa saber se a lista dela está vazia de verdade ou
  // se a consulta falhou — as duas coisas parecem iguais na tela e significam o oposto.
  const [erroPresets, setErroPresets] = useState(false);
  const [erroFornecedores, setErroFornecedores] = useState(false);
  const [erroGestores, setErroGestores] = useState(false);
  const [erroAlertas, setErroAlertas] = useState(false);
  const [carregandoFornecedores, setCarregandoFornecedores] = useState(true);

  const [editandoPreset, setEditandoPreset] = useState<Preset | null>(null);
  const [confirmImportar, setConfirmImportar] = useState(false);
  /** Fornecedor aberto no modal de detalhes — é lá que as ações vivem. */
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState<Fornecedor | null>(null);
  /** Modal de cadastro de gestores (12/08/2026). Montagem condicional: ver o próprio modal. */
  const [gerenciandoGestores, setGerenciandoGestores] = useState(false);

  /**
   * Carga da PÁGINA de fornecedores, separada do resto de propósito: trocar de página
   * não pode reconsultar presets, gestores e alertas nem piscar o loader de tela cheia.
   */
  const carregarFornecedores = useCallback(async (pagina: number) => {
    setCarregandoFornecedores(true);
    try {
      const r = await fetchFornecedores({ page: pagina, pageSize: FORNECEDORES_POR_PAGINA });
      setFornecedores(r.data);
      setTotalFornecedores(r.total);
      setErroFornecedores(false);
    } catch (error) {
      setFornecedores([]);
      setTotalFornecedores(0);
      setErroFornecedores(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os fornecedores. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregandoFornecedores(false);
    }
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    // `allSettled`: falha de um bloco não pode derrubar os outros — mas também não
    // pode passar em silêncio, que era o defeito do `.catch(() => [])` anterior.
    const [rPresets, rGestores, rAlertas] = await Promise.allSettled([
      fetchPresets(),
      fetchGestores(),
      fetchPainelAlertas(),
    ]);

    setErroPresets(rPresets.status === 'rejected');
    setPresets(rPresets.status === 'fulfilled' ? rPresets.value : []);

    setErroGestores(rGestores.status === 'rejected');
    setGestores(rGestores.status === 'fulfilled' ? rGestores.value : []);

    setErroAlertas(rAlertas.status === 'rejected');
    setAlertas(rAlertas.status === 'fulfilled' ? rAlertas.value : null);

    if (rPresets.status === 'rejected') {
      notify.errorFrom(
        rPresets.reason,
        'Não foi possível carregar a configuração do Portal de Cotações. Recarregue a página; se persistir, acione a TI.',
      );
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    carregarFornecedores(paginaFornecedores);
  }, [carregarFornecedores, paginaFornecedores]);

  const totalPaginasFornecedores = Math.max(1, Math.ceil(totalFornecedores / FORNECEDORES_POR_PAGINA));

  // Gestores são poucos e a rota devolve a lista inteira: paginação no cliente, igual às
  // entregas. `Math.min` protege a página quando a lista encurta entre duas cargas.
  const totalPaginasGestores = Math.max(1, Math.ceil(gestores.length / GESTORES_POR_PAGINA));
  const paginaGestoresValida = Math.min(paginaGestores, totalPaginasGestores);
  const gestoresPagina = gestores.slice(
    (paginaGestoresValida - 1) * GESTORES_POR_PAGINA,
    paginaGestoresValida * GESTORES_POR_PAGINA,
  );


  const somaPesos = editandoPreset
    ? editandoPreset.pesoPreco + editandoPreset.pesoPrazo + editandoPreset.pesoQualidade
    : 100;

  async function salvarPreset() {
    if (!editandoPreset) return;
    setProcessando(true);
    try {
      await atualizarPreset(editandoPreset.id, {
        nome: editandoPreset.nome,
        descricao: editandoPreset.descricao ?? undefined,
        pesoPreco: editandoPreset.pesoPreco,
        pesoPrazo: editandoPreset.pesoPrazo,
        pesoQualidade: editandoPreset.pesoQualidade,
      });
      notify.success('Preset salvo. Cotações já enviadas não mudam.');
      setEditandoPreset(null);
      await carregar();
    } catch (error) {
      notify.actionError(error, { action: 'update', subject: 'Preset' });
    } finally {
      setProcessando(false);
    }
  }

  /**
   * Recarrega a página da tabela e reabre o fornecedor do modal com o dado novo.
   *
   * Sem o segundo passo, adicionar ou remover um contato atualizaria a tabela atrás do
   * modal e deixaria o modal exibindo a lista antiga — o usuário veria a ação "não
   * acontecer" até fechar e reabrir.
   */
  const atualizarAposMudancaDeContato = useCallback(async () => {
    const r = await fetchFornecedores({ page: paginaFornecedores, pageSize: FORNECEDORES_POR_PAGINA });
    setFornecedores(r.data);
    setTotalFornecedores(r.total);
    setErroFornecedores(false);
    setFornecedorSelecionado((atual) => (atual ? r.data.find((f) => f.cnpj === atual.cnpj) ?? null : null));
  }, [paginaFornecedores]);

  return (
    <div
      className="Frame1000004012"
      style={{
        height: carregando ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: carregando ? 'center' : 'space-between',
        alignItems: carregando ? 'center' : 'flex-start',
        backgroundColor: carregando ? '#808080' : 'transparent',
      }}
    >
      {carregando && <FullScreenLoader />}

      {!carregando && (
        <div className="ConteDo recepcao-conte-do">
          <div className="recepcao-header-row">
            <div>
              <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Administração do Sistema - Cotações
                <button
                  type="button"
                  className="help-circle-btn"
                  title="Como Funciona"
                  onClick={() => setAjudaAberta(true)}
                >
                  ?
                </button>
              </div>
              <p className="admin-subtitle">Presets, fornecedores, gestores e entregas</p>
            </div>
          </div>

          {/*
            A faixa CONVIVE com o conteúdo — não substitui a tela (BUG-V27): cabeçalho,
            abas e o que carregou continuam acessíveis.
          */}
          {(erroPresets || erroFornecedores) && (
            <p className="erro-de-tela">
              Não foi possível carregar a configuração. O que aparece abaixo pode estar incompleto — recarregue a
              página antes de editar qualquer peso ou contato.
            </p>
          )}

          <div className="admin-tabs-container">
            {(
              [
                ['presets', 'Presets'],
                ['fornecedores', 'Fornecedores'],
                ['gestores', 'Gestores de compras'],
                ['resposta', 'Resposta do fornecedor'],
                ['pagamento', 'Formas de pagamento'],
                ['alertas', 'Entregas'],
                ['emails', 'E-mails ao fornecedor'],
              ] as [Aba, string][]
            ).map(([id, rotulo]) => (
              <button
                key={id}
                type="button"
                className={`admin-tab-btn ${aba === id ? 'active' : ''}`}
                onClick={() => setAba(id)}
              >
                {rotulo}
              </button>
            ))}
          </div>

          {aba === 'presets' && (
            <>
              <div className="aviso-de-tela">
                Editar um preset <strong>não afeta cotações já enviadas</strong> — os pesos são congelados no envio.
                A mudança vale só para as próximas.
              </div>
              {presets.length === 0 ? (
                <div className="admin-card">
                  <div className="admin-vazio">
                    {erroPresets
                      ? 'Não foi possível carregar os presets.'
                      : 'Nenhum preset cadastrado.'}
                  </div>
                </div>
              ) : (
                presets.map((p) => (
                  <div key={p.id} className="admin-card">
                    {editandoPreset?.id === p.id ? (
                      <>
                        <div className="admin-input-group">
                          <label className="admin-label" htmlFor={`nome-${p.id}`}>
                            Nome
                          </label>
                          <input
                            id={`nome-${p.id}`}
                            className="admin-input"
                            value={editandoPreset.nome}
                            onChange={(e) => setEditandoPreset({ ...editandoPreset, nome: e.target.value })}
                          />
                        </div>
                        <div className="admin-form-grid">
                          {(
                            [
                              ['pesoPreco', 'Preço %'],
                              ['pesoPrazo', 'Prazo %'],
                              ['pesoQualidade', 'Qualidade %'],
                            ] as ['pesoPreco' | 'pesoPrazo' | 'pesoQualidade', string][]
                          ).map(([campo, rotulo]) => (
                            <div key={campo} className="admin-input-group fixed-width" style={{ maxWidth: '120px' }}>
                              <label className="admin-label" htmlFor={`${campo}-${p.id}`}>
                                {rotulo}
                              </label>
                              <input
                                id={`${campo}-${p.id}`}
                                type="number"
                                min={0}
                                max={100}
                                className="admin-input"
                                value={editandoPreset[campo]}
                                onChange={(e) =>
                                  setEditandoPreset({
                                    ...editandoPreset,
                                    [campo]: parseInt(e.target.value, 10) || 0,
                                  })
                                }
                              />
                            </div>
                          ))}
                        </div>
                        {somaPesos !== 100 && (
                          <p className="admin-hint admin-hint-alerta">
                            Os três pesos precisam somar 100. Soma atual: {somaPesos}.
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                          <button
                            type="button"
                            className="admin-btn admin-btn-secondary"
                            onClick={() => setEditandoPreset(null)}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-primary"
                            disabled={somaPesos !== 100 || processando || erroPresets}
                            onClick={salvarPreset}
                          >
                            Salvar preset
                          </button>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
                      >
                        <div>
                          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--tema-texto, #1A1C1B)' }}>{p.nome}</p>
                          {p.descricao && (
                            <p className="admin-texto-secundario" style={{ margin: '2px 0' }}>
                              {p.descricao}
                            </p>
                          )}
                          <p className="admin-texto-secundario" style={{ margin: '4px 0 0' }}>
                            Preço {p.pesoPreco}% · Prazo {p.pesoPrazo}% · Qualidade {p.pesoQualidade}%
                            {p.pesoQualidade === 0 && ' (qualidade não influencia o score)'}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary"
                          style={{ padding: '6px 14px', fontSize: '12px' }}
                          disabled={erroPresets}
                          onClick={() => setEditandoPreset({ ...p })}
                        >
                          Editar
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </>
          )}

          {aba === 'fornecedores' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                {/*
                  O contador é do TOTAL do servidor, não do tamanho da página — com a
                  lista paginada, "20 fornecedores" seria mentira sobre a base.
                */}
                <span className="admin-texto-secundario" style={{ flex: 1 }}>
                  {erroFornecedores
                    ? 'Não foi possível verificar quantos fornecedores estão cadastrados.'
                    : `${totalFornecedores} fornecedor(es) cadastrado(s)` +
                      (totalPaginasFornecedores > 1
                        ? ` · página ${paginaFornecedores} de ${totalPaginasFornecedores}`
                        : '')}
                </span>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => setConfirmImportar(true)}
                >
                  Importar do ERP
                </button>
              </div>

              {/*
                Tabela no padrão do sistema (classes de `Recepcao.css`, as mesmas da
                Recepção e da lista de cotações). Antes era um card por fornecedor —
                com ~1000 registros importados do ERP, virava rolagem sem fim.
              */}
              <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
                {/*
                  Larguras pelo tamanho REAL do conteúdo: razão social do ERP passa de 70
                  caracteres e era o que estava truncando, enquanto "Contatos" sobrava
                  espaço vazio. A coluna Situação existe para o resto do espaço virar
                  informação em vez de ar.
                */}
                <div
                  className="TabelaContent"
                  style={{
                    ['--table-columns' as any]: '3.6fr 1.4fr 2fr 0.9fr',
                    width: '100%',
                    minWidth: '900px',
                  }}
                >
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo">
                        <div className="Opcoes">Fornecedor</div>
                        <div className="Opcoes">CNPJ</div>
                        <div className="Opcoes">Contatos</div>
                        <div className="Opcoes">Situação</div>
                      </div>
                    </div>
                  </div>

                  {carregandoFornecedores ? (
                    <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div
                        className="Item"
                        style={{ gridColumn: '1 / span 4', justifyContent: 'center', padding: '40px 0' }}
                      >
                        <div className="Data" style={{ textAlign: 'center' }}>Carregando fornecedores...</div>
                      </div>
                    </div>
                  ) : fornecedores.length === 0 ? (
                    <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div
                        className="Item"
                        style={{ gridColumn: '1 / span 4', justifyContent: 'center', padding: '40px 0' }}
                      >
                        <div className="Data" style={{ textAlign: 'center' }}>
                          {erroFornecedores
                            ? 'Não foi possível carregar os fornecedores.'
                            : 'Nenhum fornecedor. Use Importar do ERP para trazer a base e depois cadastre os contatos.'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    fornecedores.map((f) => (
                      <div key={f.cnpj} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                        {/*
                          Tabela SOMENTE LEITURA (decisão do usuário, 11/08/2026): as ações
                          vivem no modal, como na Recepção — a placa é o link lá, o nome do
                          fornecedor é o link aqui.
                        */}
                        <div className="Item">
                          <div
                            className="Data"
                            onClick={() => setFornecedorSelecionado(f)}
                            // O `title` compensa a reticência: o nome inteiro aparece no
                            // hover (e sempre no modal que o clique abre).
                            title={f.razaoSocial}
                            style={{
                              cursor: 'pointer',
                              color: '#ea5d0e',
                              fontWeight: 600,
                              textDecoration: 'underline',
                              // UMA linha por regra do Victor (12/08/2026): célula de tabela
                              // nunca quebra em duas — nome longo corta com reticências.
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                            }}
                          >
                            {f.razaoSocial}
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{f.cnpj}</div>
                        </div>

                        <div className="Item">
                          {f.contatos.length === 0 ? (
                            <div className="Data" style={{ color: 'var(--tema-texto-vermelho, #b42318)' }}>
                              Sem contato — não recebe convite
                            </div>
                          ) : (
                            <div className="Data">
                              {/* Telefone é guardado só com dígitos: exibe formatado. */}
                              {f.contatos
                                .map(
                                  (c) =>
                                    c.nome ??
                                    c.email ??
                                    (c.telefone ? formatPhoneBr(c.telefone) : null) ??
                                    'Contato',
                                )
                                .join(', ')}
                              {f.contatos.length > 1 && (
                                <span className="admin-texto-secundario"> ({f.contatos.length})</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="Item">
                          <div className="Data" style={{ color: f.inativo ? 'var(--tema-texto-vermelho, #b42318)' : 'var(--tema-texto, #1a1c1b)' }}>
                            {f.inativo ? 'Inativo' : 'Ativo'}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/*
                A paginação é do SERVIDOR (`page`/`pageSize` na rota), não recorte no
                cliente: o total vem na resposta e a troca de página recarrega.
              */}
              {fornecedores.length > 0 && (
                <Paginacao
                  paginaAtual={paginaFornecedores}
                  totalPaginas={totalPaginasFornecedores}
                  onMudarPagina={setPaginaFornecedores}
                />
              )}
            </>
          )}

          {aba === 'gestores' && (
            <div className="admin-card">
              <div className="admin-card-title">Gestores de compras</div>
              <p className="admin-hint" style={{ marginTop: 0 }}>
                Só quem está nesta lista aprova itens acima do menor preço. Administradores não têm passe livre:
                precisam estar cadastrados aqui como qualquer outro.
                {ehAdmin
                  ? ' Use "Gerenciar gestores" para adicionar ou remover.'
                  : ' A lista é mantida por um administrador.'}
              </p>

              {/*
                Botão só para ADMIN: `PUT /gestores` é ADMIN puro no Portal. TI e COMPRA
                continuam vendo a lista (ou o aviso de carga) sem um botão que só daria 403.
              */}
              {ehAdmin && !erroGestores && (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  style={{ marginBottom: 16 }}
                  onClick={() => setGerenciandoGestores(true)}
                >
                  Gerenciar gestores
                </button>
              )}

              {erroGestores ? (
                /*
                  Erro de API não pode virar afirmação de negócio (at-60): antes desta
                  entrega a falha aqui era engolida e a tela dizia "nenhum gestor
                  cadastrado" — conclusão que só cabe quando a consulta responde.
                */
                <p className="erro-de-tela">
                  Não foi possível carregar a lista de gestores, então não é possível saber quem pode aprovar. A
                  consulta da lista é restrita a administradores; se você é administrador, recarregue a página e
                  acione a TI se persistir.
                </p>
              ) : gestores.length === 0 ? (
                <p className="admin-hint admin-hint-alerta">
                  Nenhum gestor cadastrado — com a lista vazia, cotações com item acima do menor preço ficam
                  paradas.{' '}
                  {ehAdmin
                    ? 'Cadastre ao menos um gestor em "Gerenciar gestores" antes de usar o portal.'
                    : 'Peça a um administrador para cadastrar ao menos um gestor antes de usar o portal.'}
                </p>
              ) : (
                <>
                  <p className="admin-hint" style={{ marginTop: 0 }}>
                    {gestores.length} gestor(es) cadastrado(s)
                    {totalPaginasGestores > 1 ? ` · página ${paginaGestoresValida} de ${totalPaginasGestores}` : ''}
                  </p>

                  {/*
                    Tabela somente leitura: as ações vivem no modal "Gerenciar gestores",
                    como na aba de fornecedores. Paginação no cliente, como nas entregas:
                    são poucos registros e a rota devolve a lista inteira.
                  */}
                  <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
                    <div
                      className="TabelaContent"
                      style={{
                        ['--table-columns' as any]: '2.5fr 2fr',
                        width: '100%',
                        minWidth: '520px',
                      }}
                    >
                      <div className="Tabela TabelaHeader">
                        <div className="Placa">
                          <div className="TTulo">
                            <div className="Opcoes">Gestor</div>
                            <div className="Opcoes">Identificador</div>
                          </div>
                        </div>
                      </div>

                      {gestoresPagina.map((g) => (
                        <div key={g.userId} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                          <div className="Item">
                            <div className="Data" style={{ fontWeight: 600 }}>
                              {g.nome ?? 'Nome não disponível'}
                            </div>
                          </div>
                          <div className="Item">
                            <div className="Data" style={{ color: 'var(--tema-texto-2, #5e5e5e)' }}>{g.userId}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {totalPaginasGestores > 1 && (
                    <Paginacao
                      paginaAtual={paginaGestoresValida}
                      totalPaginas={totalPaginasGestores}
                      onMudarPagina={setPaginaGestores}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* at-16: condições da peça, o que o fornecedor vê no link (a senha saiu na at-17). */}
          {aba === 'resposta' && <RespostaFornecedorSection podeEditar={ehAdmin || ehTi} />}
          {/* at-10: a lista de formas de pagamento; desde a at-16 é o comprador quem escolhe uma
              delas ao gerar o pedido. Carga própria e edição só para quem pode gravar. */}
          {aba === 'pagamento' && <FormasPagamentoSection podeEditar={ehAdmin || ehTi} />}
          {/* at-15: cada forma acima ligada à cobrança do pedido no Nasajon. */}
          {aba === 'pagamento' && <CobrancaNoErpSection podeEditar={ehAdmin || ehTi} />}

          {/*
            A aba reúne os dois relógios do módulo: as ENTREGAS (o que já chegou ou atrasou) e,
            desde a at-11, o TEMPO POR ETAPA da própria cotação. São assuntos vizinhos — os dois
            respondem "o que está parado?" — e separá-los em abas faria o ADMIN procurar em duas.
          */}
          {aba === 'alertas' && (
            <>
              <AlertasEtapaSection podeEditar={ehAdmin || ehTi} />
              <EntregasSection alertas={alertas} erro={erroAlertas} />
            </>
          )}

          {/*
            at-11: os dois e-mails que o comprador dispara DEPOIS da decisão. Aba própria, e não
            junto das Integrações: lá fica o convite (que tem `{{link}}` e depende do OctaDesk),
            aqui a conversa com o fornecedor já escolhido. Carga própria, como as outras seções
            que leem settings.
          */}
          {aba === 'emails' && <EmailsFornecedorSection podeEditar={ehAdmin || ehTi} />}
        </div>
      )}

      <ConfirmacaoModal
        isOpen={confirmImportar}
        titulo="Importar fornecedores do ERP?"
        descricao={
          <>
            A identidade dos fornecedores (CNPJ, razão social, situação) é sincronizada com o ERP. Os{' '}
            <strong>contatos cadastrados aqui não são afetados</strong> — o ERP não os tem.
          </>
        }
        rotuloConfirmar="Importar"
        carregando={processando}
        onConfirmar={async () => {
          setProcessando(true);
          try {
            const r = await importarFornecedores();
            if (r.parcial) {
              // Erro conhecido de paginação do ERP: o que veio foi salvo.
              notify.warning(
                `${r.importados} novo(s) e ${r.atualizados} atualizado(s). ${r.aviso ?? ''}`,
                { autoClose: 12000, toastId: 'importacao-fornecedores-parcial' },
              );
            } else {
              notify.success(`${r.importados} novo(s) e ${r.atualizados} atualizado(s).`);
            }
            setConfirmImportar(false);
            // A importação muda a base inteira: volta para a página 1, senão a página
            // em que o usuário estava passa a apontar para outro pedaço da lista.
            setPaginaFornecedores(1);
            await carregarFornecedores(1);
          } catch (error) {
            notify.actionError(error, { action: 'create', subject: 'Cadastro de fornecedores' });
          } finally {
            setProcessando(false);
          }
        }}
        onCancelar={() => setConfirmImportar(false)}
      />

      {/*
        Montagem condicional, não prop `isOpen`: garante que o modal comece sempre do
        estado inicial (o formulário de contato não guarda rascunho de uma abertura
        anterior) — mesma razão da regra dos modais de filtro.
      */}
      {fornecedorSelecionado && (
        <FornecedorDetalhesModal
          fornecedor={fornecedorSelecionado}
          onFechar={() => setFornecedorSelecionado(null)}
          onAtualizado={atualizarAposMudancaDeContato}
        />
      )}

      {/*
        Montagem condicional + lista aplicada por prop: as duas metades da regra de modais
        de estado (at-61/at-65) — o rascunho de um "Cancelar" não sobrevive à reabertura, e
        o modal nunca abre em branco sobre uma lista que existe.
      */}
      {gerenciandoGestores && (
        <GerenciarGestoresModal
          gestoresAtuais={gestores}
          onFechar={() => setGerenciandoGestores(false)}
          // A rota devolve a lista já gravada: usar o retorno evita um GET a mais e evita
          // a janela em que a tela mostraria o estado antigo.
          onSalvo={(lista) => {
            setGestores(lista);
            setPaginaGestores(1);
          }}
        />
      )}

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Configurações do Portal"
        secoes={helpSections}
      />
    </div>
  );
};

export default PortalCotacoesConfig;
